import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
  cn,
} from '@staff-app/ui';
import type { OpticlockProjectSplit } from '@staff-app/shared-types';
import { formatHoursMinutes, formatPercent } from '../lib/duration';
import { projectColorClass } from '../lib/project-colors';


interface ProjectSplitBarProps {
  email: string;
  /** This agent's month total. */
  minutes: number;
  /** The LARGEST month total across the whole table — the scale for every bar. */
  maxMinutes: number;
  /** Ordered minutes-DESC by the server; rendered in that order. */
  byProject: OpticlockProjectSplit[];
  /** Track width/height overrides. Defaults to the 160px pill used in the dashboard table. */
  trackClassName?: string;
  /** Tooltip heading. Defaults to `email` — set it where the row identity is already obvious. */
  heading?: string;
}

/**
 * One agent's month as a stacked bar, with a hover breakdown.
 *
 * TWO NESTED PERCENTAGE LAYERS, and getting this wrong is the single easiest way to render a
 * chart that carries no information:
 *
 *   outer  = this agent's minutes / the TABLE's largest total  → how this agent compares
 *   inner  = each segment's minutes / THIS agent's total       → how their time split
 *
 * A bar scaled only to its own row total is full-width for every agent, so every row looks
 * identical and the visual says nothing. The mockup's bars are visibly different lengths.
 *
 * `style={{ width }}` is the one inline style in the feature and is unavoidable — Tailwind
 * cannot express a runtime percentage. It is always a computed number, never user text.
 *
 * TooltipProvider is mounted LOCALLY, per the existing pattern in AgentDeviceStatusDot /
 * DeviceStatusDot / PeersTableBySubnet — App.tsx mounts only <Toaster>.
 */
export function ProjectSplitBar({
  email,
  minutes,
  maxMinutes,
  byProject,
  trackClassName,
  heading,
}: ProjectSplitBarProps) {
  const outerPct = maxMinutes > 0 ? (minutes / maxMinutes) * 100 : 0;

  return (
    // delayDuration: Radix defaults to 700ms, which reads as "there is no tooltip" on a bar you
    // are sweeping across a table of rows. 150ms feels present without firing on a stray pass.
    <TooltipProvider delayDuration={150}>
      <Tooltip>
        <TooltipTrigger asChild>
          <div className={cn('h-3 w-40 overflow-hidden rounded-full bg-muted', trackClassName)}>
            <div
              className="flex h-full"
              style={{ width: `${outerPct}%` }}
              data-testid={`opticlock-bar-${email}`}
            >
              {byProject.map((s) => {
                const innerPct = minutes > 0 ? (s.minutes / minutes) * 100 : 0;
                return (
                  <div
                    key={s.projectId}
                    className={cn('h-full', projectColorClass(s.color))}
                    style={{ width: `${innerPct}%` }}
                    data-testid={`opticlock-bar-${email}-${s.projectId}`}
                  />
                );
              })}
            </div>
          </div>
        </TooltipTrigger>
        <TooltipContent data-testid={`opticlock-tooltip-${email}`}>
          {/* Leads with WHOSE time this is. On a table of bars the pointer is the only thing
              tying a tooltip to a row, so naming the agent removes the "which row am I looking
              at" guess entirely. */}
          <div className="flex min-w-[15rem] flex-col gap-1">
            <div className="flex items-baseline justify-between gap-4 border-b border-current/20 pb-1">
              <span className="truncate text-xs font-semibold">{heading ?? email}</span>
              <span className="shrink-0 text-xs font-semibold tabular-nums">
                {formatHoursMinutes(minutes)}
              </span>
            </div>
            {byProject.map((s) => (
              <span key={s.projectId} className="flex items-center gap-2 text-xs">
                <span className={cn('h-2 w-2 shrink-0 rounded-full', projectColorClass(s.color))} />
                <span className="grow truncate">{s.name}</span>
                <span className="shrink-0 tabular-nums">{formatHoursMinutes(s.minutes)}</span>
                <span className="w-14 shrink-0 text-right tabular-nums opacity-70">
                  {formatPercent(s.minutes, minutes)}
                </span>
              </span>
            ))}
            {byProject.length === 0 ? (
              <span className="text-xs opacity-70">{'No project breakdown'}</span>
            ) : null}
          </div>
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}
